/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import { RootState } from '../../../core/store/store';
import CatalogueManager, {
  type CatalogueField,
  type CatalogueColumn,
} from '../components/CatalogueManager';

/**
 * Catalogues de jeu (CM) — bonus et succès.
 * Les tables sont créées par la migration 2026_07_23_002 ; leur CONTENU se saisit ici,
 * conformément à la règle « tout ce qui est en base est modifiable au panel ».
 */

const BONUS_FIELDS: CatalogueField[] = [
  { key: 'code', label: 'Code', type: 'text', required: true, placeholder: 'reactivite' },
  { key: 'nom', label: 'Nom', type: 'text', required: true, placeholder: 'Réactivité' },
  { key: 'icone', label: 'Icône', type: 'text', placeholder: '⚡' },
  { key: 'portee', label: 'Portée', type: 'select', options: ['agent', 'cta', 'batiment'], defaultValue: 'agent' },
  { key: 'unite', label: 'Unité', type: 'text', defaultValue: '%', placeholder: '%, place, appel' },
  { key: 'valeur_min', label: 'Valeur min', type: 'number' },
  { key: 'valeur_max', label: 'Valeur max', type: 'number' },
  { key: 'premium', label: 'Premium', type: 'bool', defaultValue: false },
  { key: 'ordre', label: 'Ordre', type: 'number' },
  { key: 'actif', label: 'Actif', type: 'bool', defaultValue: true },
  { key: 'description', label: 'Description', type: 'textarea' },
];

const BONUS_COLUMNS: CatalogueColumn[] = [
  { key: 'code', label: 'Code' },
  {
    key: 'nom',
    label: 'Nom',
    render: (r) => (
      <span className="font-semibold text-white">
        {r.icone ? `${r.icone} ` : ''}
        {r.nom}
      </span>
    ),
  },
  { key: 'portee', label: 'Portée' },
  {
    key: 'valeur',
    label: 'Fourchette',
    render: (r) => `${r.valeur_min} – ${r.valeur_max} ${r.unite || ''}`.trim(),
  },
  {
    key: 'premium',
    label: 'Premium',
    render: (r) =>
      Number(r.premium) ? (
        <span className="rounded border border-amber-500/45 bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-200">
          Premium
        </span>
      ) : (
        <span className="text-slate-500">—</span>
      ),
  },
  {
    key: 'actif',
    label: 'Actif',
    render: (r) => (Number(r.actif) ? 'Oui' : <span className="text-slate-500">Non</span>),
  },
];

const SUCCES_FIELDS: CatalogueField[] = [
  { key: 'code', label: 'Code', type: 'text', required: true, placeholder: 'premieres_100_interventions' },
  { key: 'nom', label: 'Nom', type: 'text', required: true },
  { key: 'icone', label: 'Icône', type: 'text', placeholder: '🏅' },
  { key: 'categorie', label: 'Catégorie', type: 'text', placeholder: 'Opérations' },
  { key: 'condition_type', label: 'Condition (type)', type: 'text', placeholder: 'interventions_closes' },
  { key: 'condition_valeur', label: 'Condition (valeur)', type: 'number' },
  { key: 'recompense_argent', label: 'Récompense €', type: 'number' },
  { key: 'recompense_medailles', label: 'Récompense 🏅', type: 'number' },
  { key: 'ordre', label: 'Ordre', type: 'number' },
  { key: 'actif', label: 'Actif', type: 'bool', defaultValue: true },
  { key: 'description', label: 'Description', type: 'textarea' },
];

const SUCCES_COLUMNS: CatalogueColumn[] = [
  { key: 'code', label: 'Code' },
  {
    key: 'nom',
    label: 'Nom',
    render: (r) => (
      <span className="font-semibold text-white">
        {r.icone ? `${r.icone} ` : ''}
        {r.nom}
      </span>
    ),
  },
  { key: 'categorie', label: 'Catégorie' },
  {
    key: 'condition',
    label: 'Condition',
    render: (r) => (r.condition_type ? `${r.condition_type} ≥ ${r.condition_valeur}` : '—'),
  },
  {
    key: 'recompense',
    label: 'Récompense',
    render: (r) => `${Number(r.recompense_argent || 0).toLocaleString('fr-FR')} € · ${r.recompense_medailles || 0} 🏅`,
  },
  {
    key: 'obtenu_par',
    label: 'Débloqué par',
    render: (r) =>
      Number(r.obtenu_par || 0) > 0 ? (
        <span className="rounded border border-emerald-500/45 bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-200">
          {r.obtenu_par} joueur(s)
        </span>
      ) : (
        <span className="text-slate-500">—</span>
      ),
  },
];

const TABS = [
  { key: 'bonus', label: 'Bonus' },
  { key: 'succes', label: 'Succès' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

const CataloguesManagement: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const isEmbedded =
    embedded ||
    new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get('embed') === '1';
  const [tab, setTab] = useState<TabKey>('bonus');

  const hasAccess = (currentUser?.roles_id ?? 99) >= 1 && (currentUser?.roles_id ?? 99) <= 4;
  if (!hasAccess) return null;

  return (
    <div className={!isEmbedded ? 'min-h-screen relative overflow-hidden' : ''}>
      {!isEmbedded && <BackgroundImage />}
      {!isEmbedded && <ParticlesBackground />}
      {!isEmbedded && <Navbar />}
      <div className={!isEmbedded ? 'relative z-10 pt-24 pb-16' : ''}>
        <div className={!isEmbedded ? 'max-w-6xl mx-auto px-4 sm:px-6 lg:px-8' : ''}>
          {!isEmbedded && (
            <div className="mb-8 flex items-center justify-between">
              <div>
                <h1 className="mb-2 text-3xl font-bold text-white">Catalogues de jeu</h1>
                <p className="text-sm text-slate-400">
                  Bonus du personnel et succès : la liste se saisit ici, elle n&apos;est plus codée en dur.
                </p>
              </div>
              <button
                onClick={() => navigate('/panel')}
                className="flex items-center gap-2 rounded-lg border border-[#3d4fdb]/40 bg-[#3d4fdb]/20 px-4 py-2 text-sm font-semibold text-[#8ea0f5] transition-colors hover:bg-[#3d4fdb]/30"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Retour
              </button>
            </div>
          )}

          <div className="mb-3 flex items-center gap-2">
            {TABS.map((tb) => (
              <button
                key={tb.key}
                type="button"
                onClick={() => setTab(tb.key)}
                className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  tab === tb.key
                    ? 'border-emerald-500/60 bg-emerald-500/20 text-emerald-100'
                    : 'border-white/15 bg-black/30 text-slate-300 hover:bg-black/45'
                }`}
              >
                {tb.label}
              </button>
            ))}
          </div>

          {tab === 'bonus' ? (
            <CatalogueManager
              endpoint="bonus"
              title="Catalogue des bonus"
              description="Bonus tirés à l'embauche (portée agent) ou apportés par un centre (portée cta). La fourchette min/max borne le tirage."
              fields={BONUS_FIELDS}
              columns={BONUS_COLUMNS}
              emptyLabel="Aucun bonus. Le type existait déjà côté code, mais la liste restait à écrire — c'est ici."
            />
          ) : (
            <CatalogueManager
              endpoint="succes"
              title="Catalogue des succès"
              description="Le code doit rester stable : c'est lui qui est enregistré dans l'historique des joueurs (user_achievements)."
              fields={SUCCES_FIELDS}
              columns={SUCCES_COLUMNS}
              emptyLabel="Aucun succès défini."
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CataloguesManagement;
